import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import React, { useState } from 'react';
import styled from 'styled-components';

import { Button } from '~/atomic/atom/Button';
import { Checkbox } from '~/atomic/molecula/Checkbox';
import Modal from '~/atomic/molecula/Modal';
import { PageTitleStyle, TextRegStyle } from '~/atomic/Typography';
import { choosePaySelector } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.selector';
import { choosePayActions } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.slice';
import { ReceiptContactInput } from '~/feature/finance/BuyingSteps/ChoosePay/ReceiptContactInput';
import { userSelector } from '~/feature/user/user.selector';
import { validateReceiptContact } from '~/feature/validators';
import { useAppDispatch, useAppSelector } from '~/store';

export const ConfirmSaveBankCardModal = ({ onOk }: { onOk: () => void }) => {
  const [localChecked, setLocalChecked] = useState(true);
  const [contact, setContact] = useState('');
  const [contactError, setContactError] = useState('');
  const dispatch = useAppDispatch();
  const { isConfirmSaveBankCardModal, isPayLoading } = useAppSelector(choosePaySelector);
  const { user } = useAppSelector(userSelector);

  const needsContact = !user.email && !user.phone;

  const closeModal = () => {
    dispatch(choosePayActions.setIsConfirmSaveBankCardModal(false));
  };

  const validateContact = (value: string) => {
    const error = validateReceiptContact(value);
    setContactError(error);
    return !error;
  };

  const changeContactHandler = (value: string) => {
    setContact(value);
    if (!value) {
      setContactError('');
    } else if (contactError) {
      validateContact(value);
    }
  };

  const buyHandler = () => {
    if (needsContact && !validateContact(contact)) {
      return;
    }
    if (needsContact) {
      dispatch(choosePayActions.changeReceiptContact(contact));
    }
    dispatch(choosePayActions.setIsSaveBankCard(localChecked));
    onOk();
  };

  const changeSaveBankCardHandler = (e: CheckboxChangeEvent) => {
    setLocalChecked(e.target.checked);
  };

  return (
    <SCBuyModal
      open={isConfirmSaveBankCardModal}
      onCancel={closeModal}
    >
      <SCTitle>
        Оплата доступа к книге
      </SCTitle>
      {needsContact && (
        <>
          <SCDescription>
            Для отправки чека укажите ваш e-mail или номер телефона
          </SCDescription>
          <SCContactField>
            <ReceiptContactInput
              value={contact}
              onChange={changeContactHandler}
              onPressEnter={buyHandler}
              error={contactError}
              autoFocus
            />
          </SCContactField>
        </>
      )}
      <SCCheckbox
        checked={localChecked}
        onChange={changeSaveBankCardHandler}
        title={(
          <SCCheckboxText>
            Запомнить карту для оплаты следующих книг в один клик?
            <br />
            Удалить привязку карты можно в любой момент.
          </SCCheckboxText>
        )}
      />
      <SCBuyBookButton
        type="primary"
        size="large"
        onClick={buyHandler}
        isLoading={isPayLoading}
      >
        Перейти к оплате
      </SCBuyBookButton>
    </SCBuyModal>
  );
};

const SCBuyModal = styled(Modal)`
  width: 530px !important;
`;

const SCTitle = styled.h3`
  ${PageTitleStyle};
  color: var(--black-color);
  margin-bottom: 12px;
`;

const SCDescription = styled.p`
  ${TextRegStyle};
  color: var(--gray-color);
  margin-bottom: 8px;
`;

const SCCheckboxText = styled.span`
  ${TextRegStyle};
  color: var(--black-color);
  line-height: 25px;
  display: inline-flex;
  padding-left: 8px;
`;

const SCBuyBookButton = styled(Button)`
  max-width: 260px;
  width: 100%;
  height: 48px;
  font-size: 16px;
  margin: 32px auto 0;
`;

const SCContactField = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 380px;
  width: 100%;
  margin-bottom: 24px;
`;

const SCCheckbox = styled(Checkbox)`
  .ant-checkbox-wrapper {
    display: flex;
    align-items: flex-start;
  }

  .ant-checkbox {
    width: 22px;
    height: 22px;
    top: 2px;
  }

  .ant-checkbox-inner {
    width: 22px;
    height: 22px;

    &:after {
      width: 7px;
      height: 11px;
    }
  }
`;
